/**
 * MFA WebSocket Sniffer
 *
 * Intercepts Finvu WebSocket messages directly in React to capture raw server payloads
 * and the exact error messages (e.g., "6 digit PIN is required", "Invalid PIN") without
 * modifying the SDK file.
 */

let isHooked = false;

export function initMfaWsSniffer() {
	if (typeof window === 'undefined' || !window.WebSocket || isHooked) {
		return;
	}
	isHooked = true;

	const origAddEventListener = window.WebSocket.prototype.addEventListener;
	window.WebSocket.prototype.addEventListener = function (type, listener, options) {
		if (type === 'message' && typeof listener === 'function') {
			const wrappedListener = function (event) {
				handleIncomingWsData(event?.data);
				return listener.apply(this, arguments);
			};
			return origAddEventListener.call(this, type, wrappedListener, options);
		}
		return origAddEventListener.apply(this, arguments);
	};

	// Also hook onmessage setter in case property assignment is used
	try {
		const origDescriptor = Object.getOwnPropertyDescriptor(
			window.WebSocket.prototype,
			'onmessage',
		);
		if (origDescriptor && origDescriptor.set) {
			Object.defineProperty(window.WebSocket.prototype, 'onmessage', {
				set(fn) {
					if (typeof fn !== 'function') {
						return origDescriptor.set.call(this, fn);
					}
					const wrapped = function (event) {
						handleIncomingWsData(event?.data);
						return fn.apply(this, arguments);
					};
					return origDescriptor.set.call(this, wrapped);
				},
				get: origDescriptor.get,
				configurable: true,
			});
		}
	} catch (e) {
		// Ignore if descriptor modification is restricted
	}

	// Intercept send to clear previous error when a new PIN request starts
	const origSend = window.WebSocket.prototype.send;
	window.WebSocket.prototype.send = function (data) {
		try {
			const parsed = typeof data === 'string' ? JSON.parse(data) : null;
			const reqType = parsed?.header?.type || '';
			if (reqType.includes('Pin') || reqType.includes('auth')) {
				window._lastMfaWsError = null;
			}
		} catch (e) {}
		return origSend.apply(this, arguments);
	};
}

function handleIncomingWsData(rawData) {
	try {
		const parsed = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
		const payload = parsed?.payload || parsed?.body;
		if (!payload) return;

		window._lastMfaWsPayload = payload;

		if (payload.factorData) {
			window._lastMfaWsFactorData = payload.factorData;
		}

		// Extract exact error string if present
		const factorData = payload.factorData;
		const exactErr =
			factorData?.error ||
			factorData?.errorMessage ||
			(factorData?.message &&
			factorData.message !== 'MFA authentication failed' &&
			factorData.message !== 'Authentication failed'
				? factorData.message
				: null) ||
			payload?.error ||
			(payload?.message &&
			payload.message !== 'MFA authentication failed' &&
			payload.message !== 'Authentication failed'
				? payload.message
				: null);

		if (exactErr && typeof exactErr === 'string' && exactErr.trim()) {
			window._lastMfaWsError = exactErr.trim();
			console.log('[MfaWsSniffer] Captured exact server error:', window._lastMfaWsError);
		} else if (
			payload.authenticated === true ||
			payload.factorStatus === 'ENROLLED' ||
			payload.status === 'SUCCESS'
		) {
			window._lastMfaWsError = null;
		}
	} catch (e) {}
}

export function getLastMfaWsError() {
	if (typeof window === 'undefined') return null;
	return window._lastMfaWsError || null;
}

export function getMfaWsFactorData() {
	if (typeof window === 'undefined') return null;
	return window._lastMfaWsFactorData || window._lastMfaWsPayload?.factorData || null;
}

export function isMfaFactorEnrolled() {
	if (typeof window === 'undefined') return undefined;
	const factorData = window._lastMfaWsFactorData || window._lastMfaWsPayload?.factorData;
	if (factorData && factorData.enrolled !== undefined) {
		return Boolean(factorData.enrolled);
	}
	return undefined;
}

export function clearLastMfaWsError() {
	if (typeof window === 'undefined') return;
	window._lastMfaWsError = null;
}

export function resetMfaWsSession() {
	if (typeof window === 'undefined') return;
	window._lastMfaWsError = null;
	window._lastMfaWsFactorData = null;
	window._lastMfaWsPayload = null;
}
